import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CardMedia,
  Button
} from '@mui/material';
import { motion } from 'framer-motion';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpacityIcon from '@mui/icons-material/Opacity';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';
import RecyclingIcon from '@mui/icons-material/Recycling';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ScienceIcon from '@mui/icons-material/Science';
import FactoryIcon from '@mui/icons-material/Factory';
import WavesIcon from '@mui/icons-material/Waves';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { styled, alpha } from '@mui/material/styles';

// Styled components
const GlassPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 0,
  background: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid #f0f0f0',
  width: '100%',
  maxWidth: '100%',
  margin: '0',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: '#1565C0',
  }
}));

const ProcessModule = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: ({ direction = 'row' }) => direction,
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(4),
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  transition: 'box-shadow 0.3s ease',
  width: '100%',
  maxWidth: '100%',
  borderRadius: 0,
  borderLeft: ({ color = '#1565C0' }) => `4px solid ${color}`,
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100% !important',
      padding: '0 !important',
      marginBottom: theme.spacing(3),
    },
  },
}));

const ModuleTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#1565C0',
  marginBottom: theme.spacing(2),
  position: 'relative',
  display: 'inline-block',
  fontSize: '1.5rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase'
}));

const FeatureAvatar = styled(Avatar)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 198, 207, 0.15)',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '50px',
  fontWeight: 600,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

const AnimatedWaterDrop = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: '300px',
  height: '300px',
  borderRadius: '80% 0 55% 50% / 55% 0 80% 50%',
  background: 'linear-gradient(135deg, rgba(0, 198, 207, 0.2), rgba(79, 172, 254, 0.2))',
  filter: 'blur(40px)',
  zIndex: -1,
}));

const IndustrialETP = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 0,
        background: '#f5f7fa',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Animated background elements */}
      <AnimatedWaterDrop
        initial={{ x: -150, y: -50, opacity: 0.5 }}
        animate={{ 
          x: [-150, -120, -150],
          y: [-50, -30, -50],
          opacity: [0.5, 0.7, 0.5],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 15,
          ease: 'easeInOut'
        }}
        style={{ top: '10%', left: '5%' }}
      />
      
      <AnimatedWaterDrop
        initial={{ x: 350, y: 250, opacity: 0.3 }}
        animate={{ 
          x: [350, 380, 350],
          y: [250, 230, 250],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 18,
          ease: 'easeInOut'
        }}
        style={{ bottom: '10%', right: '5%' }}
      />
      
      <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', px: { xs: 0, md: 0 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'left', mb: 0, backgroundColor: '#1565C0', color: 'white', py: 6, px: { xs: 3, md: 6 }, width: '100%' }}>
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'white', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Industrial Effluent Treatment Plants
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: '800px', color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
              Advanced solutions for industrial wastewater treatment and recycling
            </Typography>
          </motion.div>
        </Box>
        
        {/* Main Content - Process Flow Modules */}
        <Box sx={{ mt: 0, mb: 0, width: '100%', backgroundColor: '#ffffff', py: 5, px: { xs: 3, md: 6 } }}>
          <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 3, mb: 5 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#333333' }}>
              Industrial ETP Process Flow
            </Typography>
          </Box>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ width: '100%' }}
          >
            {/* Pre-Treatment Module */}
            <ProcessModule color="#1565C0">
              <Box sx={{ width: '30%', pr: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/screener.jpg" 
                  alt="Pre-Treatment System" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5">
                  Pre Treatment
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Fully automated for higher efficiency with no chemical process/chemical sludge.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Biological System with DO & pH sensors" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Advanced Eco-friendly Biological system" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Cooling Tower & Screener Integration" />
                  </ListItem>
                </List>
              </Box>
            </ProcessModule>

            {/* DAF System Module */}
            <ProcessModule direction="row-reverse" color="#0277BD">
              <Box sx={{ width: '30%', pl: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/daf.jpg" 
                  alt="DAF System" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5" color="#00C6CF">
                  DAF SYSTEM
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Dissolved Air Flotation system for efficient solid-liquid separation.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Reduction of Organic load" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Removal of Free Oil & Grease" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Sturdy Axial Rotating Scrapper Paddles" />
                  </ListItem>
                </List>
              </Box>
            </ProcessModule>

            {/* Clarifier/Lamella Settler Module */}
            <ProcessModule color="#01579B">
              <Box sx={{ width: '30%', pr: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/lamle.jpg" 
                  alt="Lamella Settler" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5">
                  Clarifier & Lamella Settler
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Advanced sedimentation systems for efficient solid-liquid separation.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="High efficiency clarification" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Compact design with smaller footprint" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Optimized for industrial applications" />
                  </ListItem>
                </List>
              </Box>
            </ProcessModule>

            {/* Membrane Bio Reactor Module */}
            <ProcessModule direction="row-reverse" color="#00838F">
              <Box sx={{ width: '30%', pl: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/mf.jpg" 
                  alt="Membrane Bio Reactor" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5">
                  Membrane Bio Reactor
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Advanced membrane technology for superior filtration and treatment efficiency.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Lesser Foot Print" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Nominal pore size of 0.03 μm" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Advanced membrane technology" />
                  </ListItem>
                </List>
              </Box>
            </ProcessModule>

            {/* Micro/Ultra Filtration Module */}
            <ProcessModule color="#006064">
              <Box sx={{ width: '30%', pr: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/ro.jpg" 
                  alt="Micro/Ultra Filtration" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5">
                  Micro / Ultra Filtration
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Pre-filtration system for RO with advanced automated backwash capabilities.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Increases Life of RO Membranes" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Removes Colloidal & Suspended matter" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Automated Rapid Filtration & Backwash sequence" />
                  </ListItem>
                </List>
              </Box>
            </ProcessModule>

            {/* Reverse Osmosis System Module */}
            <ProcessModule direction="row-reverse" color="#004D40">
              <Box sx={{ width: '30%', pl: 3 }}>
                <Box 
                  component="img" 
                  src="/images/etp/ro.jpg" 
                  alt="Reverse Osmosis System" 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover',
                    borderRadius: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }}
                />
              </Box>
              <Box sx={{ width: '70%' }}>
                <ModuleTitle variant="h5">
                  Reverse Osmosis System
                </ModuleTitle>
                <Typography variant="body1" paragraph>
                  Advanced membrane-based separation technology for producing high-purity water from industrial effluent.
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                        System Specifications
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <FilterAltIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Membrane Type" 
                            secondary="Thin Film Composite (TFC)" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <SpeedIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Operating Pressure" 
                            secondary="10-16 bar" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <WaterDropIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Recovery Rate" 
                            secondary="75-85%" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                        Performance Benefits
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="TDS Reduction" 
                            secondary="Up to 99%" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Energy Efficiency" 
                            secondary="Low-energy membranes with energy recovery devices" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Automation" 
                            secondary="Fully automated operation with CIP system" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                </Grid>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1565C0' }}>
                  Applications:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Chip size="small" label="Boiler Feed Water" />
                  <Chip size="small" label="Process Water" />
                  <Chip size="small" label="Wastewater Reclamation" />
                  <Chip size="small" label="Zero Liquid Discharge" />
                </Box>
              </Box>
            </ProcessModule>
          </motion.div>

          {/* MBR Technology Section */}
          <Box sx={{ width: '100%', mt: 0, backgroundColor: '#f5f7fa', py: 6, px: { xs: 3, md: 6 } }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, color: '#333333', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
              Advanced MBR Technology Options
            </Typography>

            <Grid container spacing={0}>
              {/* Ceramic MBR */}
              <Grid item xs={12} md={6}>
                <GlassPaper elevation={1}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1565C0', mb: 3, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Ceramic MBR Technology
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          component="img"
                          src="/images/etp/c-mbr-1.png"
                          alt="Ceramic MBR 1"
                          sx={{ 
                            height: 180,
                            mr: 2,
                            borderRadius: 0,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Box
                          component="img"
                          src="/images/etp/c-2mbr-2.png"
                          alt="Ceramic MBR 2"
                          sx={{ 
                            height: 180,
                            borderRadius: 0,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="body1" paragraph>
                    Our ceramic MBR systems offer exceptional durability and filtration performance for industrial applications.
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                          Technical Specifications
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <FilterAltIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Membrane Material" 
                              secondary="Al₂O₃/ZrO₂/TiO₂ Ceramic" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <WaterDropIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Pore Size" 
                              secondary="0.1-0.5 μm (MF) / 0.01-0.1 μm (UF)" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <SpeedIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Max Operating Temp" 
                              secondary="Up to 90°C" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                          Key Advantages
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Chemical Resistance" 
                              secondary="pH range 0-14, excellent solvent resistance" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Lifespan" 
                              secondary="10+ years vs 3-5 years for polymeric membranes" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Flux Rate" 
                              secondary="30-150 LMH (liters/m²/hour)" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1565C0' }}>
                    Ideal Applications:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
                    <Chip size="small" label="High-strength Industrial Wastewater" />
                    <Chip size="small" label="Oil & Gas Produced Water" />
                    <Chip size="small" label="Chemical Processing" />
                    <Chip size="small" label="Textile Effluent" />
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ 
                      mt: 2,
                      borderRadius: 0,
                      py: 1.5,
                      px: 3,
                      backgroundColor: '#1565C0',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Learn More About Ceramic MBR
                  </Button>
                </GlassPaper>
              </Grid>
              
              {/* Polymer MBR */}
              <Grid item xs={12} md={6}>
                <GlassPaper elevation={1}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1565C0', mb: 3, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Polymer MBR Technology
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          component="img"
                          src="/images/etp/p-mbr-1.jpg"
                          alt="Polymer MBR 1"
                          sx={{ 
                            height: 180,
                            mr: 2,
                            borderRadius: 0,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Box
                          component="img"
                          src="/images/etp/p-mbr-2.jpg"
                          alt="Polymer MBR 2"
                          sx={{ 
                            height: 180,
                            borderRadius: 0,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="body1" paragraph>
                    Our polymer-based MBR systems provide cost-effective treatment solutions with excellent filtration capabilities.
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                          Technical Specifications
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <FilterAltIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Membrane Material" 
                              secondary="PVDF/PES/PTFE Polymeric" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <WaterDropIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Configurations" 
                              secondary="Hollow Fiber, Flat Sheet, Tubular" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <SpeedIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Max Operating Temp" 
                              secondary="Up to 40-45°C" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid #e0e0e0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                          Key Advantages
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Cost Efficiency" 
                              secondary="30-50% lower capital cost than ceramic" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Packing Density" 
                              secondary="Up to 1,000 m²/m³ membrane area" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Energy Consumption" 
                              secondary="0.3-0.6 kWh/m³ treated water" 
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1565C0' }}>
                    Ideal Applications:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
                    <Chip size="small" label="Municipal Wastewater" />
                    <Chip size="small" label="Food & Beverage Industry" />
                    <Chip size="small" label="Pharmaceutical Wastewater" />
                    <Chip size="small" label="Leachate Treatment" />
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ 
                      mt: 2,
                      borderRadius: 0,
                      py: 1.5,
                      px: 3,
                      backgroundColor: '#1565C0',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Learn More About Polymer MBR
                  </Button>
                </GlassPaper>
              </Grid>
            </Grid>
          </Box>
          
          {/* Advanced Features Section */}
          <Box sx={{ width: '100%', backgroundColor: '#ffffff', py: 6, mt: 0, px: { xs: 3, md: 6 } }}>
            <Box sx={{ width: '100%', mx: 'auto' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, color: '#333333', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                  Advanced Features & Technologies
                </Typography>
                
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <FeatureAvatar sx={{ mx: 'auto' }}>
                        <EngineeringIcon />
                      </FeatureAvatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Custom Engineering
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tailored solutions designed to meet your specific industry requirements and wastewater characteristics
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <FeatureAvatar bgcolor={alpha('#4FACFE', 0.1)} sx={{ color: '#4FACFE', mx: 'auto' }}>
                        <SettingsIcon />
                      </FeatureAvatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Automation & Control
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Advanced SCADA systems for real-time monitoring, control, and optimization of your ETP operations
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <FeatureAvatar bgcolor={alpha('#00C6CF', 0.1)} sx={{ mx: 'auto' }}>
                        <RecyclingIcon />
                      </FeatureAvatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Resource Recovery
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Technologies to recover valuable resources from wastewater, turning waste into value
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IndustrialETP;